import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { 
  TrendingUp, 
  Gem, 
  Bitcoin, 
  DollarSign,
  Target,
  Shield,
  Zap,
  ArrowRight,
  Check
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("token");

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    interests: [],
    project_goals: [],
    experience_level: "beginner",
    risk_tolerance: "moderate"
  });

  const interests = [
    { id: "stocks", label: "Stocks", icon: <TrendingUp className="w-5 h-5" />, desc: "Apple, Tesla, NVIDIA..." },
    { id: "commodities", label: "Commodities", icon: <Gem className="w-5 h-5" />, desc: "Gold, Oil, Silver..." },
    { id: "crypto", label: "Crypto", icon: <Bitcoin className="w-5 h-5" />, desc: "Bitcoin, Ethereum..." },
    { id: "forex", label: "Forex", icon: <DollarSign className="w-5 h-5" />, desc: "EUR/USD, GBP/USD..." }
  ];

  const goals = [
    { id: "growth", label: "Growth", desc: "Track growth trends" },
    { id: "income", label: "Data Tracking", desc: "Regular updates & info" },
    { id: "safety", label: "Research", desc: "Deep analysis data" },
    { id: "speculation", label: "Time Saving", desc: "Quick insights" }
  ];

  const riskLevels = [
    { id: "conservative", label: "Basic", icon: <Shield className="w-5 h-5" />, desc: "Simple tracking needs" },
    { id: "moderate", label: "Standard", icon: <Target className="w-5 h-5" />, desc: "Balanced approach" },
    { id: "aggressive", label: "Advanced", icon: <Zap className="w-5 h-5" />, desc: "Deep research needs" }
  ];

  const experienceLevels = [
    { id: "beginner", label: "Beginner", desc: "Just starting out" },
    { id: "intermediate", label: "Intermediate", desc: "Some experience" },
    { id: "advanced", label: "Advanced", desc: "Seasoned researcher" }
  ];

  const toggleInterest = (id) => {
    setPreferences(prev => ({
      ...prev,
      interests: prev.interests.includes(id)
        ? prev.interests.filter(i => i !== id)
        : [...prev.interests, id]
    }));
  };

  const toggleGoal = (id) => {
    setPreferences(prev => ({
      ...prev,
      project_goals: prev.project_goals.includes(id)
        ? prev.project_goals.filter(g => g !== id)
        : [...prev.project_goals, id]
    }));
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/user/preferences`, preferences, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      updateUser({ ...user, onboarding_completed: true, preferences });
      toast.success("Profile set up! Let's find your opportunities.");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div 
              key={s}
              className={`h-2 w-16 ${s <= step ? 'bg-amber-500' : 'bg-zinc-800'} transition-colors`}
            />
          ))}
        </div>

        {/* Step 1: Interests */}
        {step === 1 && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <h1 
                className="text-4xl font-bold tracking-tight uppercase mb-2"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                What <span className="text-amber-500">Markets</span> Interest You?
              </h1>
              <p className="text-zinc-400">Select all that apply</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {interests.map((item) => (
                <button
                  key={item.id}
                  onClick={() => toggleInterest(item.id)}
                  className={`p-6 border text-left transition-all ${
                    preferences.interests.includes(item.id)
                      ? 'bg-amber-500/10 border-amber-500'
                      : 'bg-[#111111] border-zinc-800 hover:border-zinc-700'
                  }`}
                  data-testid={`interest-${item.id}`}
                >
                  <div className={`w-10 h-10 flex items-center justify-center mb-3 ${
                    preferences.interests.includes(item.id) ? 'text-amber-500' : 'text-zinc-400'
                  }`}>
                    {item.icon}
                  </div>
                  <p className="font-semibold mb-1">{item.label}</p>
                  <p className="text-sm text-zinc-500">{item.desc}</p>
                  {preferences.interests.includes(item.id) && (
                    <Check className="absolute top-3 right-3 w-5 h-5 text-amber-500" />
                  )}
                </button>
              ))}
            </div>

            <Button 
              onClick={() => setStep(2)}
              disabled={preferences.interests.length === 0}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-6"
              data-testid="next-step-1"
            >
              Continue <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Goals & Experience */}
        {step === 2 && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <h1 
                className="text-4xl font-bold tracking-tight uppercase mb-2"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Your <span className="text-amber-500">Goals</span>
              </h1>
              <p className="text-zinc-400">What are you looking to achieve?</p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-8">
              {goals.map((goal) => (
                <button
                  key={goal.id}
                  onClick={() => toggleGoal(goal.id)}
                  className={`p-4 border text-left transition-all ${
                    preferences.project_goals.includes(goal.id)
                      ? 'bg-amber-500/10 border-amber-500'
                      : 'bg-[#111111] border-zinc-800 hover:border-zinc-700'
                  }`}
                  data-testid={`goal-${goal.id}`}
                >
                  <p className="font-semibold mb-1">{goal.label}</p>
                  <p className="text-xs text-zinc-500">{goal.desc}</p>
                </button>
              ))}
            </div>

            <p className="text-sm text-zinc-400 mb-3">Experience Level</p>
            <div className="flex gap-3 mb-8">
              {experienceLevels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => setPreferences(prev => ({ ...prev, experience_level: level.id }))}
                  className={`flex-1 p-3 border text-center transition-all ${
                    preferences.experience_level === level.id
                      ? 'bg-amber-500/10 border-amber-500'
                      : 'bg-[#111111] border-zinc-800 hover:border-zinc-700'
                  }`}
                  data-testid={`experience-${level.id}`}
                >
                  <p className="font-semibold text-sm">{level.label}</p>
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 border-zinc-700"
              >
                Back
              </Button>
              <Button 
                onClick={() => setStep(3)}
                disabled={preferences.project_goals.length === 0}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider"
                data-testid="next-step-2"
              >
                Continue <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Risk Tolerance */}
        {step === 3 && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <h1 
                className="text-4xl font-bold tracking-tight uppercase mb-2"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Risk <span className="text-amber-500">Tolerance</span>
              </h1>
              <p className="text-zinc-400">How much risk can you handle?</p>
            </div>

            <div className="space-y-4 mb-8">
              {riskLevels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => setPreferences(prev => ({ ...prev, risk_tolerance: level.id }))}
                  className={`w-full p-6 border flex items-center gap-4 text-left transition-all ${
                    preferences.risk_tolerance === level.id
                      ? 'bg-amber-500/10 border-amber-500'
                      : 'bg-[#111111] border-zinc-800 hover:border-zinc-700'
                  }`}
                  data-testid={`risk-${level.id}`}
                >
                  <div className={`w-12 h-12 flex items-center justify-center ${
                    preferences.risk_tolerance === level.id ? 'text-amber-500 bg-amber-500/20' : 'text-zinc-400 bg-zinc-800'
                  }`}>
                    {level.icon}
                  </div>
                  <div>
                    <p className="font-semibold mb-1">{level.label}</p>
                    <p className="text-sm text-zinc-500">{level.desc}</p>
                  </div>
                  {preferences.risk_tolerance === level.id && (
                    <Check className="ml-auto w-6 h-6 text-amber-500" />
                  )}
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => setStep(2)}
                className="flex-1 border-zinc-700"
              >
                Back
              </Button>
              <Button 
                onClick={handleComplete}
                disabled={loading}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider"
                data-testid="complete-onboarding"
              >
                {loading ? "Saving..." : "Start Exploring"} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-zinc-600 mt-8">
          Your preferences help us personalize your ECOLIONS experience
        </p>
      </div>
    </div>
  );
}
